import { useEffect, useMemo, useState } from "react";

import { Link, useNavigate } from "react-router-dom";

import { createColumnHelper } from "@tanstack/react-table";
import {
  EyeIcon,
  LinkIcon,
  PlusIcon,
  WrenchIcon,
} from "@heroicons/react/24/outline";

import { Button } from "../../components/base/Button";
import { Table } from "../../components/base/Table/Table";
import {
  ApplicationVisability,
  IApplication,
} from "../../models/Application/Application";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import { ApplicationQueryKeys } from "../../data/Application/keys";
import { enumToSelectOptions } from "../../utils/enumToKeyValue";
import { SystemApplicationSettingsModal } from "../../components/ApplicationSettings/SystemApplicationSettingsModal";
import { ApplicationSettingsConnectionStringModal } from "../../components/ApplicationSettings/ApplicationSettingsConnectionStringModal";

const colHelper = createColumnHelper<IApplication>();

export function Applications() {
  const { setInfo } = usePageNameContext();
  const navigate = useNavigate();

  const [appSettingsOpen, setAppSettingsOpen] = useState(false);
  const [appSettingsAppId, setAppSettingsAppId] = useState<string>();

  const [connStringOpen, setConnStringOpen] = useState(false);
  const [connStringString, setConnStringString] = useState<string>();

  const handleOpenConnString = (connString: string) => {
    setConnStringString(connString);
    setConnStringOpen(true);
  };

  const handleOpenAppSettings = (appId: string) => {
    setAppSettingsAppId(appId);
    setAppSettingsOpen(true);
  };

  const cols = useMemo(
    () => [
      colHelper.accessor("id", { header: "Application ID" }),
      colHelper.accessor("name", { header: "Application Name" }),
      colHelper.accessor("visability", {
        header: "Visability",
        cell: (info) =>
          enumToSelectOptions(ApplicationVisability)[
            info.row.original.visability
          ].label,
      }),
      colHelper.display({
        header: "Config",
        cell: (info) =>
          info.row.original.system === true ? (
            <div className="flex items-center space-x-4">
              <LinkIcon
                className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
                onClick={() =>
                  handleOpenConnString(
                    info.row.original.systemApplicationSettingsConnectionString
                  )
                }
              />
              <WrenchIcon
                className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
                onClick={() => handleOpenAppSettings(info.row.original.id)}
              />
            </div>
          ) : (
            <p className="text-gray-600 text-xs italic">Per Instance</p>
          ),
      }),
      colHelper.display({
        header: "Actions",
        cell: (info) => (
          <div className="flex items-center space-x-4">
            <EyeIcon
              className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
              onClick={() => navigate(`/application/${info.row.original.id}`)}
            />
          </div>
        ),
      }),
    ],
    [navigate]
  );

  useEffect(() => {
    setInfo({
      name: "Applications",
      desc: "View and create applications",
      pageTitle: "Applications",
    });
  }, []);

  return (
    <>
      <SystemApplicationSettingsModal
        open={appSettingsOpen}
        setOpen={setAppSettingsOpen}
        applicationId={appSettingsAppId!}
      />
      <ApplicationSettingsConnectionStringModal
        open={connStringOpen}
        setOpen={setConnStringOpen}
        connectionString={connStringString}
      />
      <div className="space-y-6">
        <div className="flex justify-between">
          <Link to="new">
            <Button className="flex items-center space-x-2">
              <PlusIcon className="h-5 w-5 -ml-1" />
              <p>Create Application</p>
            </Button>
          </Link>
        </div>
        <Table
          url="/application"
          queryKey={ApplicationQueryKeys.all()}
          columns={cols}
        />
      </div>
    </>
  );
}
